import EventEmitter from 'eventemitter3'
import { NetLoader, MSE, GapService, BandwidthService, ERR, StreamingError, EVENT, isMediaPlaying, Buffer, Logger, getVideoPlaybackQuality, softDecodeProbe } from 'xgplayer-streaming-shared'
import { Logger as TransmuxerLogger } from 'xgplayer-transmuxer'
import { M3U8Parser } from './parser'
import { Playlist, BufferService } from './services'
import { getOption } from './options'
import { clamp } from './utils'

const logger = new Logger('hls')

export class Hls extends EventEmitter {
  /** @type {HTMLMediaElement | null} */
  media = null

  /** @type {import('./options').HlsOption} */
  _opts = null

  /** @type {BufferService} */
  _bufferService = null
  /** @type {GapService} */
  _gapService = null
  _playlist = new Playlist()
  _m3u8Loader = null
  _segmentLoader = null
  _keyLoader = null

  _m3u8Url = ''
  _m3u8RefreshInterval = 2000
  _m3u8RefreshTimer = null
  _lastSegment = null
  _firstTimeToLoad = true

  _tickTimer = null
  _tickInterval = 500

  _segmentProcessing = false

  _reloadOnPlay = false
  _urlSwitching = false

  constructor (opts) {
    super()

    this._opts = getOption(opts)
    this.media = this._opts.media || document.createElement('video')
    this._opts.media = null

    opts = this._opts

    this._m3u8Loader = new NetLoader({
      ...opts.fetchOptions,
      responseType: 'text',
      retry: opts.retryCount,
      retryDelay: opts.retryDelay,
      timeout: opts.loadTimeout,
      onRetryError: this._onLoaderRetry,
      responseFilter: this._onLoaderResponseFilter
    })

    this._segmentLoader = new NetLoader({
      ...opts.fetchOptions,
      retry: opts.retryCount,
      retryDelay: opts.retryDelay,
      timeout: opts.loadTimeout,
      onRetryError: this._onLoaderRetry,
      responseFilter: this._onLoaderResponseFilter
    })

    this._keyLoader = new NetLoader({
      ...opts.fetchOptions,
      retry: opts.retryCount,
      retryDelay: opts.retryDelay,
      timeout: opts.loadTimeout,
      onRetryError: this._onLoaderRetry,
      responseFilter: this._onLoaderResponseFilter
    })

    this.media.addEventListener('play', this._onPlay)
    this.media.addEventListener('seeking', this._onSeeking)
    this.media.addEventListener('timeupdate', this._onTimeupdate)

    this._bufferService = new BufferService(this, opts.softDecode ? this.media : undefined)
    this._bandwidthService = new BandwidthService()

    if (!opts.softDecode) {
      this._gapService = new GapService()
    }
  }

  get version () {
    return __VERSION__
  }

  get isLive () {
    return this._opts.isLive
  }

  speedInfo () {
    return {
      speed: this._bandwidthService.getLatestSpeed(),
      avgSpeed: this._bandwidthService.getAvgSpeed()
    }
  }

  bufferInfo (maxHole = 0.1) {
    return Buffer.info(Buffer.get(this.media), this.media?.currentTime, maxHole)
  }

  playbackQuality () {
    return getVideoPlaybackQuality(this.media)
  }

  async load (url) {
    if (!this._bufferService) return
    this._m3u8Url = url || this._m3u8Url
    await this._reset()
    if (!this._m3u8Url) throw new StreamingError(ERR.OPTION, null, null, 'm3u8 url is missing')

    this._tickTimer = setTimeout(this._tick, this._tickInterval)

    await this._loadM3U8()

    if (this.isLive) {
      this._bufferService.setLiveSeekableRange(0, 0xffffffff)
    }

    await this._loadSegment()
  }

  async replay (isPlayEmit) {
    if (!this.media) return
    this._opts.startTime = 0
    this._opts.softDecode ? this.load() : (await this.load())
    this._reloadOnPlay = false
    return this.media.play(!isPlayEmit)
  }

  async switchURL (url, startTime = 0) {
    this._opts.startTime = startTime
    this._firstTimeToLoad = true
    this._urlSwitching = true
    this._opts.softDecode ? this.load(url) : (await this.load(url))
    this._reloadOnPlay = false
    return this.media.play(true)
  }

  async destroy () {
    if (!this.media) return
    this.removeAllListeners()
    this._playlist.reset()
    this.media.removeEventListener('play', this._onPlay)
    this.media.removeEventListener('seeking', this._onSeeking)
    this.media.removeEventListener('timeupdate', this._onTimeupdate)
    await Promise.all([this._clear(), this._bufferService.destroy()])
    this._bufferService = null
    this._playlist = null
    this.media = null
  }

  static isSupported (softDecode) {
    if (softDecode) return softDecodeProbe()
    return MSE.isSupported() || softDecodeProbe()
  }

  static enableLogger () {
    Logger.enable()
    TransmuxerLogger.enable()
  }

  static disableLogger () {
    Logger.disable()
    TransmuxerLogger.disable()
  }

  _loadM3U8 = async () => {
    const opts = this._opts
    if (!this._m3u8Url || this._m3u8Loader.loading) return
    clearTimeout(this._m3u8RefreshTimer)

    let [m3u8Response, playlist] = await this._loadAndParseM3U8()
    if (!playlist) return

    if (playlist.isMaster) {
      this._m3u8Url = this._playlist.currentLevel.url
      ;[m3u8Response, playlist] = await this._loadAndParseM3U8()
      if (!playlist) return
    }

    const currentLevel = this._playlist.currentLevel

    if (opts.isLive == null) {
      opts.isLive = currentLevel.live
    }

    if (opts.isLive) {
      this._playlist.clearOldSegment()

      if (!opts.targetLatency) {
        opts.targetLatency = playlist.segments.reduce((t, c) => (t + c.duration), 0)
      }

      if (!opts.maxLatency) {
        opts.maxLatency = opts.targetLatency * 2
      }

      const segmentTime = (currentLevel.segments[0]?.duration || 0) * 1000
      this._m3u8RefreshTimer = setTimeout(this._loadM3U8, Math.max(segmentTime - m3u8Response.stats.loadTime, 1000))
    } else {
      await this._bufferService.updateDuration(currentLevel.totalDuration)
    }
  }

  async _loadAndParseM3U8 () {
    let m3u8Response
    try {
      this.emit(EVENT.LOAD_START, { url: this._m3u8Url })
      logger.debug('load m3u8', this._m3u8Url)
      m3u8Response = await this._m3u8Loader.load(this._m3u8Url)
      this.emit(EVENT.LOAD_COMPLETE, { url: this._m3u8Url })
    } catch (error) {
      const seg = this._playlist.lastSegment
      if (seg) {
        seg.isLast = true
      }
      this._emitError(StreamingError.network(error))
      return []
    }
    if (!m3u8Response) return []

    let playlist
    try {
      playlist = M3U8Parser.parse(m3u8Response.data, this._m3u8Url)
    } catch (error) {
      this._emitError(new StreamingError(ERR.M3U8))
      return []
    }

    if (playlist) this._playlist.upsertPlaylist(playlist)

    return [m3u8Response, playlist]
  }

  _loadSegment = async () => {
    if (!this.media || this._keyLoader.loading || this._segmentLoader.loading) return
    if (this._firstTimeToLoad) {
      if (!this.isLive && this._opts.startTime) {
        this.media.currentTime = this._opts.startTime
        this._playlist.moveSegmentPointer(
          this._playlist.findSegmentIndexByTime(this._opts.startTime) || 0
        )
      }
      this._firstTimeToLoad = false
    }
    const currentTime = this.media.currentTime
    const prevSeg = this._playlist.prevSegment
    const curSeg = this._playlist.currentSegment
    if (!curSeg || (prevSeg && prevSeg.end - currentTime >= this._opts.preloadTime)) return
    this._playlist.moveSegmentPointer()

    let appended = false
    this._segmentProcessing = true
    try {
      appended = await this._reqAndBufferSegment(curSeg)
    } catch (error) {
      return this._emitError(StreamingError.create(error))
    } finally {
      this._segmentProcessing = false
    }

    if (appended && this._urlSwitching) {
      this._urlSwitching = false
      this.emit(EVENT.SWITCH_URL_SUCCESS, { url: this._m3u8Url })
    }

    if (curSeg.isLast) {
      if (appended) this._end()
    } else {
      this._loadSegment()
    }
  }

  async _reqAndBufferSegment (seg) {
    let segResponse
    try {
      this.emit(EVENT.LOAD_START, { url: seg.url })
      logger.debug('load segment', seg)
      segResponse = await this._segmentLoader.load({
        url: seg.url,
        range: seg.byteRange
      })
      if (segResponse) {
        const time = segResponse.stats.totalTime
        const byteLength = segResponse.contentLength
        this._bandwidthService.addRecord(byteLength, time)
        this.emit(EVENT.SPEED, { time, byteLength })
      }
      this.emit(EVENT.LOAD_COMPLETE, { url: seg.url })
    } catch (error) {
      return this._emitError(StreamingError.network(error))
    }
    if (!segResponse) return
    let chunk = segResponse.data

    if (seg.key && seg.key.url) {
      let key = this._playlist.getKey(seg.key.url)
      if (!key) {
        let keyResponse
        try {
          this.emit(EVENT.LOAD_START, { url: seg.key.url })
          keyResponse = await this._keyLoader.load(seg.key.url)
          this.emit(EVENT.LOAD_COMPLETE, { url: seg.key.url })
        } catch (error) {
          return this._emitError(StreamingError.network(error))
        }
        if (!keyResponse) return
        key = keyResponse.data
        this._playlist.setKey(seg.key.url, key)
      }
      chunk = await this._bufferService.decryptBuffer(chunk, key, seg.key.iv)
    }

    const discontinuity = this._lastSegment ? this._lastSegment.cc !== seg.cc : true
    const contiguous = this._lastSegment ? (this._lastSegment.sn + 1) === seg.sn : false
    await this._bufferService.appendBuffer(chunk, discontinuity, contiguous, seg.start)
    await this._bufferService.evictBuffer(this._opts.bufferBehind)
    this._lastSegment = seg
    return true
  }

  _onPlay = () => {
    if (this._reloadOnPlay) {
      this._reloadOnPlay = false
      this.replay(true)
    }
  }

  _onSeeking = () => {
    if (!this.media) return

    const seekTime = this.media.currentTime
    const seekRange = this._playlist.seekRange

    if (seekRange) {
      const newSeekTime = clamp(seekTime, seekRange[0] + 0.1, seekRange[1] - 0.1)
      if (Math.abs(seekTime - newSeekTime) >= 0.1) {
        this.media.currentTime = newSeekTime
        return
      }
    }

    const prevSeg = this._playlist.prevSegment

    if (prevSeg) {
      const info = Buffer.info(Buffer.get(this.media), seekTime, 0.1)
      if (info.end && info.end >= prevSeg.end) return
    }

    const segIndex = this._playlist.findSegmentIndexByTime(seekTime)
    const seg = this._playlist.getSegmentByIndex(segIndex)
    if (segIndex == null || !seg || seg === prevSeg) return

    logger.debug('seek to', seekTime, seg)

    this._playlist.moveSegmentPointer(segIndex)

    this._keyLoader.cancel()
    this._segmentLoader.cancel()
    this._tickImmediately()
  }

  _onTimeupdate = () => {
    const opts = this._opts
    if (opts.isLive && opts.maxLatency && opts.targetLatency && this.media) {
      const liveEdge = this._playlist.liveEdge
      if (!liveEdge) return

      const latency = liveEdge - this.media.currentTime
      if (latency >= opts.maxLatency) {
        logger.debug('latency jump', latency)
        this.media.currentTime = liveEdge - opts.targetLatency
      }
    }
  }

  async _clear () {
    clearTimeout(this._m3u8RefreshTimer)
    clearTimeout(this._tickTimer)
    await Promise.all([
      this._m3u8Loader.cancel(),
      this._keyLoader.cancel(),
      this._segmentLoader.cancel()
    ])
  }

  async _reset () {
    this._lastSegment = null
    this._firstTimeToLoad = true
    this._reloadOnPlay = false
    this._playlist.reset()
    this._bandwidthService.reset()
    await this._clear()
    return this._bufferService.reset()
  }

  _end () {
    this._clear()
    if (this._bufferService) {
      this._bufferService.endOfStream()
    }
  }

  _tickImmediately () {
    clearTimeout(this._tickTimer)
    this._tickTimer = setTimeout(this._tick)
  }

  _tick = () => {
    clearTimeout(this._tickTimer)
    if (!this.media) return

    this._tickTimer = setTimeout(this._tick, this._tickInterval)

    const media = this.media

    if (Buffer.end(Buffer.get(media)) < 0.1 || !media.readyState) return

    if (isMediaPlaying(media)) {
      if (!this._segmentProcessing) {
        this._loadSegment()
      }

      if (this._gapService) {
        this._gapService.do(media, this._opts.maxJumpDistance, this.isLive)
      }
    } else {
      if (media.readyState < 2 && this._gapService) {
        this._gapService.do(media, this._opts.maxJumpDistance, this.isLive)
      }
      if (
        this.isLive &&
        !this._reloadOnPlay &&
        !media.seeking &&
        !this._playlist.inDvrWindow(media.currentTime, this._opts.disconnectTime)
      ) {
        this._m3u8Loader.cancel()
        this._segmentLoader.cancel()
        clearTimeout(this._m3u8RefreshTimer)
        this._reloadOnPlay = true
      }
    }
  }

  _emitError (error, endOfStream = true) {
    logger.table(error)
    logger.error(error)
    logger.error(this.media?.error)

    if (this._urlSwitching) {
      this._urlSwitching = false
      this.emit(EVENT.SWITCH_URL_FAILED, error)
    }

    this.emit(EVENT.ERROR, error)
    if (endOfStream) this._end()
  }

  _onLoaderRetry = (error, retryTime) => {
    this.emit(EVENT.LOAD_RETRY, {
      error: StreamingError.network(error),
      retryTime
    })
  }

  _onLoaderResponseFilter = (url, response, stats) => {
    this.emit(EVENT.TTFB, { url, responseUrl: response.url, elapsed: stats?.loadTime })
    this.emit(EVENT.LOAD_RESPONSE_HEADERS, { headers: response.headers })
    if (this._opts.fetchOptions?.responseFilter) {
      return this._opts.fetchOptions.responseFilter(url, response, stats)
    }
  }
}

try {
  if (__DEV__ || localStorage.getItem('xgd')) {
    Hls.enableLogger()
  } else {
    Hls.disableLogger()
  }
} catch (error) {
  // ignore
}
